import type { Person } from '@local/common-lib';
import {
  type AppearanceSettings,
  defaultAppearanceSettings,
} from '../theme.js';

const APPEARANCE_SETTINGS_KEY = 'appearance-settings';
const APPEARANCE_SETTINGS_EVENT = 'appearance-settings-updated';

const isColorMode = (
  value: unknown,
): value is AppearanceSettings['colorMode'] =>
  value === 'dark' || value === 'light';

const normalizeAppearance = (value: unknown): AppearanceSettings => {
  if (!value || typeof value !== 'object') {
    return defaultAppearanceSettings;
  }

  const appearance = value as Record<string, unknown>;
  return {
    colorMode: isColorMode(appearance.colorMode)
      ? appearance.colorMode
      : defaultAppearanceSettings.colorMode,
    largeText:
      typeof appearance.largeText === 'boolean'
        ? appearance.largeText
        : defaultAppearanceSettings.largeText,
  };
};

export const getAppearanceSettings = (): AppearanceSettings => {
  const value = window.localStorage.getItem(APPEARANCE_SETTINGS_KEY);
  if (!value) return defaultAppearanceSettings;

  try {
    return normalizeAppearance(JSON.parse(value));
  } catch {
    return defaultAppearanceSettings;
  }
};

export const setAppearanceSettings = (appearance: AppearanceSettings): void => {
  window.localStorage.setItem(
    APPEARANCE_SETTINGS_KEY,
    JSON.stringify(appearance),
  );
  window.dispatchEvent(new Event(APPEARANCE_SETTINGS_EVENT));
};

export const setAppearanceSettingsFromProfile = (
  appearance?: Person.Appearance,
): void => {
  setAppearanceSettings(normalizeAppearance(appearance));
};

export const onAppearanceSettingsChanged = (
  handler: () => void,
): (() => void) => {
  window.addEventListener(APPEARANCE_SETTINGS_EVENT, handler);
  return () => window.removeEventListener(APPEARANCE_SETTINGS_EVENT, handler);
};
